import api from 'config/api'
import Error from 'next/error'

export interface IFetchCaptcha {
  success: number
  challenge: string
  gt: string
  new_captcha: boolean
}

export const EMessageTypes = {
  CONFIRM_LOAN_CREATE: 'CONFIRM_LOAN_CREATE',
  CONFIRM_LOAN_REPAYMENT: 'CONFIRM_LOAN_REPAYMENT',
  CONFIRM_EARN_CREATE: 'CONFIRM_EARN_CREATE',
  CONFIRM_EARN_WITHDRAW: 'CONFIRM_EARN_WITHDRAW',
  MONITOR_LIST: 'MONITOR_LIST',
  SECOND_CREDENTIAL: 'SECOND_CREDENTIAL',
  TFA_ACTIVATION: 'TFA_ACTIVATION',
  TFA_DEACTIVATION: 'TFA_DEACTIVATION',
  NORA_CONTROL_WITHDRAWAL: 'NORA_CONTROL_WITHDRAWAL',
  NORA_CONTROL_CONVERSION: 'NORA_CONTROL_CONVERSION',
  COLLATERAL_RETURN: 'COLLATERAL_RETURN',
  CONFIRM_WITHDROWAL: 'CONFIRM_WITHDROWAL',
  CONFIRM_WHITELIST_ADDING: 'CONFIRM_WHITELIST_ADDING',
  NORA_CONTROL_TRANSFER: 'NORA_CONTROL_TRANSFER',
  CHANGE_EMAIL: 'CHANGE_EMAIL',
  CHANGE_EMAIL_NEW_EMAIL: 'CHANGE_EMAIL_NEW_EMAIL',
  CHANGE_PHONE: 'CHANGE_PHONE',
  AUTOINCREASE_SWITCH: 'AUTOINCREASE_SWITCH',
  FALLBACK_REMOVE_2FA: 'FALLBACK_REMOVE_2FA',
  FALLBACK_REMOVE_2FA_BACKUP: 'FALLBACK_REMOVE_2FA_BACKUP',
  SET_FALLBACK_EMAIL: 'SET_FALLBACK_EMAIL',
  SET_FALLBACK_EMAIL_NEW_EMAIL: 'SET_FALLBACK_EMAIL_NEW_EMAIL',
  NORA_CONTROL_PROCESS_DOUBLE_DEPOSIT: 'NORA_CONTROL_PROCESS_DOUBLE_DEPOSIT',
  NORA_CONTROL_EXCHANGE_REFUND: 'NORA_CONTROL_EXCHANGE_REFUND',
  REGISTER_NORA_USER: 'REGISTER_NORA_USER',
  NORA_CONTROL_LOAN_RECOVERY: 'NORA_CONTROL_LOAN_RECOVERY',
  CHANGE_USER_ACTIVE_2FA: 'CHANGE_USER_ACTIVE_2FA',
  SET_ADMIN_APPROVED_FAILED: 'SET_ADMIN_APPROVED_FAILED',
  NORA_CONTROL_SWITCH_JOB_STOP: 'NORA_CONTROL_SWITCH_JOB_STOP',
  NORA_CONTROL_CHANGE_DYNAMIC_PERCENT: 'NORA_CONTROL_CHANGE_DYNAMIC_PERCENT',
  NORA_CONTROL_CHANGE_USER_EMAIL: 'NORA_CONTROL_CHANGE_USER_EMAIL',
  NORA_CONTROL_UPDATE_APR_DISCOUNT: 'NORA_CONTROL_UPDATE_APR_DISCOUNT',
  NORA_CONTROL_CHANGE_USER_APR_TEMPORARY_DISCOUNT: 'NORA_CONTROL_CHANGE_USER_APR_TEMPORARY_DISCOUNT',
} as const

interface Withdrawal {
  loanId?: string
  amount?: string | number
  currency?: string
  payoutAddress?: string
  payoutExtraId?: string
  externalId?: string
  feePaidBy?: string
  fromCurrency?: string
  fromNetwork?: string
  toCurrency?: string
  toNetwork?: string
  fromAmount?: number
  address?: string
  extraId?: string
  type?: string
  flow?: string
  is_profit?: boolean
}

interface Conversion {
  fromAmount: string
  fromCurrency: string
  toCurrency: string
  from: string
  to: string
}

interface Refund {
  sub_id?: string
  exchange_id?: string
  to?: string
  amount?: string
  currency?: string
}

interface Transfer {
  from: string
  to: string
  amount: string
  currency: string
  externalId?: string
}

interface DoubleDeposit {
  sub_id: string
  parent_id: string
  is_related: boolean
}

interface IChangeUserActive2fa {
  userId: string
  isActive2fa: boolean
}

interface ISetAdminApprovedFailed {
  adminApprovedFailed: boolean
  txType: 'transaction' | 'transfer'
  transactionOrTransferId: string
}

interface IUpdateAprDiscount {
  userId: string
  aprDiscount: string
}

interface SendSlackNotificationDto {
  message: string
  channel: string
}

interface ValidateAddressDto {
  is_memo_required: boolean
  is_error_occurred?: boolean
  is_address_valid: boolean
  is_tag_valid: boolean
  result: boolean
  error?: string
}

interface SwitchJobStop {
  job_id: string
  is_stopped: boolean
}

interface ChangeUserAprTemporaryDiscount {
  user_id: string
  apr_temporary_discount: number
  apr_temporary_discount_quota: number
}

export const sendVerificationCode = async ({
  loan_id,
  earn_id,
  phone,
  email,
  newEmail,
  newPhone,
  type,
  captcha,
  withdrawal,
  overwrite,
  conversion,
  transfer,
  changeUserActive2fa,
  setAdminApprovedFailed,
  switchJobStop,
  changeUserAprTemporaryDiscount,
}: {
  loan_id?: string
  earn_id?: string
  phone?: string
  email?: string
  newEmail?: string
  newPhone?: string
  type: typeof EMessageTypes[keyof typeof EMessageTypes]
  captcha: string
  withdrawal?: Withdrawal
  conversion?: Conversion
  overwrite?: any
  transfer?: Transfer
  switchJobStop?: SwitchJobStop
  changeUserActive2fa?: IChangeUserActive2fa
  setAdminApprovedFailed?: ISetAdminApprovedFailed
  changeUserAprTemporaryDiscount?: ChangeUserAprTemporaryDiscount
}): Promise<{
  tokens: string[]
  isActive2fa: boolean
  error?: string
  backupEmail?: string
}> => {
  try {
    const { data } = await api.post('/v2/utils/verification-code/send', {
      params: {
        loan_id,
        earn_id,
        type,
        ...(email ? { email: email } : { phone: phone }),
        ...{ newEmail: newEmail },
        ...{ newPhone: newPhone },
        captcha,
        switchJobStop,
        withdrawal,
        overwrite,
        conversion,
        transfer,
        changeUserActive2fa,
        setAdminApprovedFailed,
        changeUserAprTemporaryDiscount,
      },
    })

    return {
      tokens: data.response.tokens,
      isActive2fa: data.response.is_active_2fa,
      backupEmail: data.response.backupEmail,
    }
  } catch (err) {
    let errorMsg = 'Unknown error'

    if (err.response) {
      const msg = err?.response?.message || err?.response?.error || ''

      if (msg?.includes('Failed to send sms')) {
        errorMsg = 'Send Error'
      } else if (msg?.includes('exceeded')) {
        errorMsg = msg
      } else {
        errorMsg = msg || 'Server error'
      }
    } else if (
      err.message?.includes('Failed to fetch') ||
      err.message?.includes('Network Error') ||
      err.message?.includes('net::ERR_NETWORK_CHANGED')
    ) {
      errorMsg = 'Network Error'
    }

    return {
      tokens: [],
      isActive2fa: null,
      error: errorMsg,
    }
  }
}

interface RegisterNoraUser {
  login: string
  password: string
  email: string
}

interface LoanRecovery {
  loan_id?: string
  compensation_amount?: string
}

interface ChangePairPercent {
  from_currency: string
  to_currency: string
  percent: string
}
interface ChangeUserEmail {
  old_email: string
  new_email: string
}

export const sendVerificationCodeWithoutCaptcha = async ({
  loan_id,
  earn_id,
  phone,
  email,
  type,
  withdrawal,
  overwrite,
  conversion,
  transfer,
  action,
  new_email,
  doubleDeposit,
  refund,
  nora_user_to_register,
  repaymentCurrencyCode,
  repaymentCurrencyNetwork,
  backupEmail,
  loanRecovery,
  changeUserActive2fa,
  setAdminApprovedFailed,
  switchJobStop,
  change_pair_percent,
  change_user_email,
  updateAprDiscount,
  changeUserAprTemporaryDiscount,
}: {
  loan_id?: string
  earn_id?: string
  phone?: string
  email?: string
  new_email?: string
  type: typeof EMessageTypes[keyof typeof EMessageTypes]
  withdrawal?: Withdrawal
  refund?: Refund
  conversion?: Conversion
  doubleDeposit?: DoubleDeposit
  overwrite?: any
  transfer?: Transfer
  switchJobStop?: SwitchJobStop
  action?: string
  nora_user_to_register?: RegisterNoraUser
  loanRecovery?: LoanRecovery
  repaymentCurrencyCode?: string
  repaymentCurrencyNetwork?: string
  backupEmail?: string
  changeUserActive2fa?: IChangeUserActive2fa
  setAdminApprovedFailed?: ISetAdminApprovedFailed
  change_pair_percent?: ChangePairPercent

  change_user_email?: ChangeUserEmail
  updateAprDiscount?: IUpdateAprDiscount
  changeUserAprTemporaryDiscount?: ChangeUserAprTemporaryDiscount
}): Promise<{
  tokens: string[]
  isActive2fa: boolean
  toEmail: string
  error?: string
}> => {
  try {
    const { data } = await api.post('/v2/utils/verification-code/send-without-captcha', {
      params: {
        loan_id,
        earn_id,
        type,
        ...(email ? { email: email } : { phone: phone }),
        ...{ newEmail: new_email },
        switchJobStop,
        withdrawal,
        refund,
        overwrite,
        loanRecovery,
        conversion,
        transfer,
        action,
        doubleDeposit,
        nora_user_to_register,
        repaymentCurrencyCode,
        repaymentCurrencyNetwork,
        backupEmail,
        changeUserActive2fa,
        setAdminApprovedFailed,
        change_pair_percent,
        change_user_email,
        updateAprDiscount,
        changeUserAprTemporaryDiscount,
      },
    })

    return {
      tokens: data.response.tokens,
      isActive2fa: data.response.is_active_2fa,
      toEmail: data.response.toEmail,
    }
  } catch (error) {
    return {
      tokens: [],
      isActive2fa: null,
      toEmail: null,
      error: error?.response?.message || error?.response?.error || ''
    }
  }
}

export const sendVerificationCodeRateLimited = async ({
  loan_id,
  earn_id,
  phone,
  email,
  newEmail,
  type,
  withdrawal,
  overwrite,
  conversion,
  user_repayment_amount,
  repaymentCurrencyCode,
  repaymentCurrencyNetwork,
  withdraw_address,
  withdraw_amount,
}: {
  loan_id?: string
  user_repayment_amount?: string
  earn_id?: string
  phone?: string
  email?: string
  newEmail?: string
  type: typeof EMessageTypes[keyof typeof EMessageTypes]
  withdrawal?: Withdrawal
  conversion?: Conversion
  overwrite?: any
  repaymentCurrencyCode?: string
  repaymentCurrencyNetwork?: string
  withdraw_address?: string
  withdraw_amount?: string
}): Promise<{
  tokens: string[]
  isActive2fa: boolean
}> => {
  try {
    const { data } = await api.post('/v2/utils/verification-code/send-rate-limited', {
      params: {
        loan_id,
        earn_id,
        type,
        ...(email ? { email: email } : { phone: phone }),
        ...{ newEmail: newEmail },
        user_repayment_amount,
        withdrawal,
        overwrite,
        conversion,
        repaymentCurrencyCode,
        repaymentCurrencyNetwork,
        withdraw_address,
        withdraw_amount,
      },
    })
    return {
      tokens: data.response.tokens,
      isActive2fa: data.response.is_active_2fa,
    }
  } catch (err) {
    throw new Error(JSON.parse(JSON.stringify(err)))
  }
}

export const getVerifiedToken = async (params: { code: string; verification_token: string }): Promise<{
  result: boolean
  response: string
  message: string
}> => {
  const { data } = await api.post('/v2/utils/verification-code/verify', {
    params: params,
  })

  return { result: data.result, response: data.response.token, message: data.message }
}

export const getVerifiedTokenRateLimited = async (params: {
  code: string
  verification_token: string
}): Promise<{
  result: boolean
  response: string
  message: string
}> => {
  const { data } = await api.post('/v2/utils/verification-code/verify/rate-limited', {
    params: params,
  })

  return { result: data.result, response: data.response.token, message: data.message }
}

export const getGeolocation = async (): Promise<{
  result: boolean
  response: string
}> => {
  const { data } = await api.get('/v2/utils/geolocation')
  return { result: data.result, response: data.response }
}

export const resendVerificationCode = async (params: {
  verification_token: string
  captcha: string
}): Promise<string> => {
  try {
    const { data } = await api.post('/v2/utils/verification-code/resend', {
      params: params,
    })

    return data.response.token
  } catch (ignore) {
    return params.verification_token
  }
}

export const fetchCaptcha = async (): Promise<IFetchCaptcha> => {
  try {
    const { data } = await api.get('/v2/utils/captcha')
    return data.response
  } catch (ignore) {
    return null
  }
}

export const validateAddressByNetworkAndCode = async (params: {
  address: string
  network: string
  code?: string
  tag?: string
}): Promise<ValidateAddressDto> => {
  try {
    const { data } = await api.post('/v2/utils/validate/address', {
      params: params,
    })

    return {
      is_memo_required: data.response?.is_memo_required,
      is_address_valid: data.response?.is_address_valid,
      is_tag_valid: data.response?.is_tag_valid,
      is_error_occurred: false,
      result: !data?.response?.message?.includes('not valid'),
    }
  } catch (error) {
    const res = {
      is_memo_required: false,
      is_address_valid: false,
      is_tag_valid: false,
      is_error_occurred: false,
      result: false,
      error: error?.response?.message || error?.response?.error || ''
    }

    return error?.status === 400
      ? res
      : {
          is_error_occurred: true,
          result: true,
          ...res,
        }
  }
}

export const sendMessage = async (params: {
  message: string
  email: string
  name?: string
}): Promise<boolean | string> => {
  try {
    const { data } = await api.post('/v2/utils/send-message', {
      params: params,
    })
    return data.result
  } catch (e) {
    return e?.response?.message ?? ''
  }
}

export const _fetchSettings = async (): Promise<{
  ltv_options: string[]
  ltv_default_option: string
}> => {
  try {
    const { data } = await api.get('/v2/utils/settings')
    return data.response
  } catch (e) {
    return {
      ltv_options: [],
      ltv_default_option: null,
    }
  }
}

export const sendSlackNotification = async (params: SendSlackNotificationDto): Promise<void> => {
  try {
    await api.post('/v2/utils/report-error', { params })
  } catch (e) {
    console.error(e)
  }
}

export const getDownloadLink = async (): Promise<{
  expiresIn: number
  url: string
}> => {
  try {
    const { data } = await api.post('/v2/utils/generate-apk-link')
    return data.response
  } catch (e) {
    return null
  }
}

export const fetchReferral = async (params: {
  referral_code: string
}): Promise<{
  loan_discount: string
  ltv_discounts: Record<string, string>
}> => {
  try {
    const { data } = await api.post('/v2/utils/validate/referral-code', { params })

    return { 
      loan_discount: data?.response?.loan_discount,
      ltv_discounts: data?.response?.ltv_discounts 
    }
  } catch (ignore) {
    return undefined
  }
}